import {
  disableBodyScroll,
  enableBodyScroll,
  type BodyScrollOptions,
} from 'body-scroll-lock-upgrade';
import {
  useCallback,
  useEffect,
  useRef,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { twJoin } from 'tailwind-merge';

/**
 * Prevent touch-scroll for iOS.
 * At the time of writing, the types are confusing the event and the element.
 * See https://github.com/rick-liruixin/body-scroll-lock-upgrade/issues/21
 */
const BODY_SCROLL_LOCK_OPTIONS: BodyScrollOptions = {
  allowTouchMove: (el) => {
    while (el instanceof HTMLElement && el !== document.body) {
      if (el.getAttribute('body-scroll-lock-ignore') !== null) return true;
      // @ts-expect-error The library types are broken
      el = el.parentElement;
    }
    return false;
  },
};

type TriggerProps = {
  children: ReactNode;
  className: string;
  onTrigger(): void;
};

export const Trigger = ({ children, className, onTrigger }: TriggerProps) => {
  const triggerRef = useRef<HTMLDivElement | null>(null);

  const handleClick = ({ target }: MouseEvent<HTMLDivElement>) => {
    if (!(target instanceof HTMLElement)) return;
    if (
      // NOTE Use `target` rather than `currentTarget` in order to skip
      //      intermediary elements that will also receive the event.
      target === triggerRef.current ||
      target.parentElement === triggerRef.current
    ) {
      onTrigger();
    }
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent | React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Esc' || event.key === 'Escape') {
        onTrigger();
      }
    },
    [onTrigger],
  );

  useEffect(() => {
    // NOTE When unmounting the ref becomes null, so we save a ref to it
    const savedLocalRef = triggerRef.current;
    document.body.addEventListener('keydown', handleKeyDown);
    if (triggerRef.current) {
      disableBodyScroll(triggerRef.current, BODY_SCROLL_LOCK_OPTIONS);
    }
    return () => {
      if (savedLocalRef) {
        enableBodyScroll(savedLocalRef);
      }
      document.body.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <div
      className={twJoin(className, 'h-full w-full')}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      ref={triggerRef}
      role="presentation"
    >
      <div className="h-full w-full" body-scroll-lock-ignore="true">
        {children}
      </div>
    </div>
  );
};

Trigger.displayName = 'Trigger';
